import { cloneElement, isValidElement, useEffect, useId, useMemo, useRef, useState } from 'react'

export const Check = ({ size = 12, w = 3 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={w} strokeLinecap="round"><path d="M4 12.5l5 5L20 6.5" /></svg>
)

export const Sill = ({ width = 64, style }) => (
  <div className="sill" style={{ width, margin: '12px 0 18px', ...style }} />
)

/** Planning Center's own mark, from their official brand asset kit — cropped
 *  to the icon square rather than the full wordmark, since it always sits
 *  next to text that already says "Planning Center". */
export function PcoIcon({ size = 18 }) {
  return (
    <svg width={size} height={size * (43 / 42)} viewBox="0 0 42 43" fill="none"
         xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flex: '0 0 auto' }}>
      <rect x="5" y="8" width="31" height="28" fill="white" />
      <path d="M27.6963 17.2871C27.8245 17.249 27.9532 17.3451 27.9532 17.4788L27.9532 23.8135C27.9532 23.9021 27.8949 23.9801 27.8099 24.0053L21.2687 25.9381C21.0834 25.9929 20.8861 25.9928 20.7008 25.9378L14.1899 24.0054C14.105 23.9802 14.0468 23.9022 14.0468 23.8136V17.4788C14.0468 17.3451 14.1755 17.249 14.3037 17.2871L19.7946 18.9168C20.58 19.1499 21.42 19.1499 22.2054 18.9168L27.6963 17.2871Z" fill="#2266F7" />
      <path fillRule="evenodd" clipRule="evenodd" d="M23.5833 1.34521C21.8908 0.88493 20.1092 0.88493 18.4167 1.34521L5.19168 4.94185C2.12971 5.77458 0 8.59583 0 11.8193V32.1807C0 35.4042 2.12972 38.2254 5.19169 39.0581L18.4167 42.6548C20.1092 43.1151 21.8908 43.1151 23.5833 42.6548L36.8083 39.0581C39.8703 38.2254 42 35.4042 42 32.1807V11.8193C42 8.59583 39.8703 5.77458 36.8083 4.94185L23.5833 1.34521ZM14.3037 28.1232C14.1755 28.0852 14.0468 28.1812 14.0468 28.3149V32.6921C14.0468 33.119 13.6423 33.43 13.2298 33.3203L10.483 32.5901C10.1982 32.5144 10 32.2566 10 31.962V15.4491C10 13.6983 11.7564 12.447 13.4913 12.9619L20.7008 15.1016C20.8861 15.1566 21.0834 15.1568 21.2688 15.102L28.5087 12.9619C30.2436 12.447 32 13.6983 32 15.4491L32 24.9239C32 26.0663 31.2275 27.0752 30.0955 27.4111L22.2054 29.7529C21.42 29.986 20.58 29.986 19.7946 29.7529L14.3037 28.1232Z" fill="#2266F7" />
    </svg>
  )
}

export function Progress({ step, total }) {
  return (
    <div className="prog">
      <span className="prognum">{step} / {total}</span>
      <div className="progbar"><i style={{ width: `${(step / total) * 100}%` }} /></div>
    </div>
  )
}

/**
 * The save indicator. `role="status"` matters as much as the dot: an autosave
 * that only reports visually leaves a screen-reader user with no answer to
 * "did that land", which is the same gap the dot exists to close.
 */
export function Saved({ state }) {
  const label = { saving: 'Saving…', saved: 'Saved just now', error: "Couldn't save", idle: 'Saved' }[state]
  return (
    <div className={`saved ${state}`} role="status" aria-live="polite">
      <span className="d" aria-hidden="true" /> {label}
    </div>
  )
}

/**
 * The button that answers you.
 *
 * `.btn.loading`, `.btn-label` and `.btn-spin` were specced in styles.css and
 * then used by nothing — every async action in the app instead swapped its own
 * label, which reflows the button mid-press and says nothing until the label
 * changes. One primitive, so a pending action looks the same everywhere.
 */
export function Button({ busy, disabled, children, className = 'btn btn-primary', ...rest }) {
  return (
    <button {...rest} className={`${className}${busy ? ' loading' : ''}`}
            disabled={busy || disabled} aria-busy={busy || undefined}>
      {busy && <span className="btn-spin" aria-hidden="true" />}
      <span className="btn-label">{children}</span>
    </button>
  )
}

/** Inline pending mark for the small pill controls, where a full spinner won't fit. */
export const MiniSpin = () => <span className="mini-spin" aria-hidden="true" />

/**
 * The label used to sit next to the control rather than pointing at it, so
 * every input in the app announced as an unnamed text field. Handing the child
 * a generated id and matching it with htmlFor fixes the lot in one place —
 * callers don't change. A child that already has an id keeps it, and anything
 * that isn't a single element falls back to the old markup.
 */
export function Field({ label, children }) {
  const id = useId()
  const child = isValidElement(children) && !children.props.id
    ? cloneElement(children, { id })
    : children
  return <div className="field"><label className="label" htmlFor={id}>{label}</label>{child}</div>
}

export function Switch({ on, onChange, disabled }) {
  return (
    <button type="button" className={`sw${on ? ' on' : ''}`} disabled={disabled}
            aria-pressed={on} onClick={() => onChange(!on)}><span className="k" /></button>
  )
}

export function ToggleRow({ title, hint, on, onChange, disabled }) {
  return (
    <div className="toggle-row">
      <span className="tl">{title}{hint && <small>{hint}</small>}</span>
      <Switch on={on} onChange={onChange} disabled={disabled} />
    </div>
  )
}

export function TrustRow({ ok = true, title, hint }) {
  return (
    <div className="trustrow">
      <span className={`tick${ok ? '' : ' off'}`}>{ok ? <Check /> : '·'}</span>
      <span className="tt">{title}{hint && <small>{hint}</small>}</span>
    </div>
  )
}

const SECTION_LABEL = {
  verse: 'Verse', chorus: 'Chorus', bridge: 'Bridge', bridge_2: 'Bridge 2', tag: 'Tag',
}

/** Only verses carry a number — "Chorus 1" reads wrong on a lyric sheet. */
export const sectionLabel = (s) => {
  const base = SECTION_LABEL[s.section_type] ?? s.section_type
  return s.section_type === 'verse' ? `${base} ${s.section_number ?? 1}` : base
}

export const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] ?? '').join('').toUpperCase() || '?'

/**
 * A stable colour class for a person's avatar, hashed from whatever identifies
 * them (a user id, or their name when there is no id to hand).
 *
 * Stable is the whole requirement: the same writer must be the same colour on
 * every screen and every reload, or the colour is worse than none — it looks
 * like it means something and doesn't. Hashing the id rather than the array
 * position gets that for free, and survives the list being sorted differently.
 * The eight tones live in styles.css; see the note there about contrast.
 */
/**
 * The eight person tones, as values.
 *
 * These are the same eight colours as the `.tone-0` … `.tone-7` classes in
 * styles.css and must stay in step with them — a person's colour has to be
 * the same colour wherever it is drawn, whether that's a CSS class on an
 * initials circle or an inline fill on a profile wash. Keep both lists
 * together when either changes.
 */
export const AVATAR_TONES = [
  '#2E7D8F', '#B23A48', '#3F7D46', '#5B54B8',
  '#2B6CB0', '#7A5F1A', '#A14A78', '#B4541F',
]

const toneIndex = (seed = '') => {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return Math.abs(h) % AVATAR_TONES.length
}

export const avatarTone = (seed = '') => `tone-${toneIndex(seed)}`

/** The same tone as a value, for fills CSS classes can't reach. */
export const avatarColour = (seed = '') => AVATAR_TONES[toneIndex(seed)]

/**
 * The wash behind a face on a profile: that person's own tone, curved and
 * fading out at the top.
 *
 * Their tone and not the app's accent, because an amber band on every
 * profile would be chrome, and chrome is the one thing a profile shouldn't
 * be — two writers' pages should not be the same page with the names
 * swapped. Derived from the tone the avatar already uses rather than a
 * second generator: the wash and the face have to be the same person's
 * colour, or the head of the screen reads as two people.
 */
export const personWash = (seed = '') => {
  const c = avatarColour(seed)
  return `linear-gradient(168deg, ${c} 0%, ${c}d9 58%, ${c}a6 100%)`
}

export function Spinner() { return <div className="spinner" /> }

export function DoneIcon() {
  return <div className="done-ic"><svg width="26" height="26" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M4 12.5l5 5L20 6.5" /></svg></div>
}

export const isVideo = (mime = '') => String(mime).startsWith('video/')

export const mmss = (s) =>
  Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '—'

/** The waveform bar heights are deterministic (seeded off index, not random)
    so a re-render never redraws the "waveform" under someone's finger. */
export const waveformBars = () =>
  Array.from({ length: 46 }, (_, i) => 5 + Math.round(Math.abs(Math.sin(i * 0.7)) * 17 + ((i * 37) % 5)))

/**
 * The scrubber: elapsed on the left, total on the right, drag anywhere on the
 * track. Spotify's shape, but keeping Beseter's waveform rather than swapping
 * in a plain rule — the bars are what say "this is a recording of someone
 * singing", and a flat line says nothing.
 *
 * Drag position is held locally while the pointer is down. A media element
 * only reports currentTime a few times a second, so following `pos` alone
 * leaves the playhead lagging behind the finger, which reads as broken rather
 * than slow.
 *
 * `touch-action: none` in the CSS is load-bearing: without it the browser
 * claims the gesture for scrolling and the drag dies on the first vertical
 * wobble, which on a phone is every drag.
 */
export function Waveform({ pos = 0, len = 0, onSeek, disabled }) {
  const trackEl = useRef(null)
  const bars = useMemo(waveformBars, [])
  const [drag, setDrag] = useState(null)   // 0..1 while a pointer is down, else null

  const seekable = !disabled && !!len && !!onSeek
  // ?? not ||, because a drag to the very start is 0 and must not fall through.
  const pct = drag ?? (len ? Math.min(1, Math.max(0, pos / len)) : 0)
  const shown = drag !== null ? drag * len : pos

  const fractionAt = (clientX) => {
    const r = trackEl.current?.getBoundingClientRect()
    if (!r?.width) return 0
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width))
  }

  const down = (e) => {
    if (!seekable) return
    e.currentTarget.setPointerCapture(e.pointerId)
    setDrag(fractionAt(e.clientX))
  }
  const move = (e) => { if (drag !== null) setDrag(fractionAt(e.clientX)) }
  const up = (e) => {
    if (drag === null) return
    const f = fractionAt(e.clientX)
    setDrag(null)
    onSeek(f)
  }

  const key = (e) => {
    if (!seekable) return
    const step = e.shiftKey ? 30 : 5   // seconds
    const to = (s) => { e.preventDefault(); onSeek(Math.min(1, Math.max(0, s / len))) }
    if (e.key === 'ArrowRight') to(pos + step)
    else if (e.key === 'ArrowLeft') to(pos - step)
    else if (e.key === 'Home') to(0)
    else if (e.key === 'End') to(len)
  }

  return (
    <div ref={trackEl}
         className={`wave${seekable ? ' seekable' : ''}${drag !== null ? ' dragging' : ''}`}
         role="slider" tabIndex={seekable ? 0 : -1}
         aria-label="Seek"
         aria-valuemin={0} aria-valuemax={Math.round(len) || 0}
         aria-valuenow={Math.round(shown) || 0} aria-valuetext={mmss(shown)}
         aria-disabled={seekable ? undefined : true}
         onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
         onKeyDown={key}>
      <span className="wave-bars" aria-hidden="true">
        {bars.map((h, i) => (
          <i key={i} style={{ height: h }} className={i / bars.length <= pct ? 'lit' : ''} />
        ))}
      </span>
      <span className="wave-head" aria-hidden="true" style={{ left: `${pct * 100}%` }} />
    </div>
  )
}

/**
 * One player for both. Audio keeps the waveform strip from the mockup; video
 * gets a real frame, because a reviewer watching a room sing needs to see it.
 */
export function Player({ src, mime, duration, filename }) {
  const el = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [pos, setPos] = useState(0)
  const [len, setLen] = useState(duration || 0)
  const [failed, setFailed] = useState(false)
  const video = isVideo(mime)

  useEffect(() => { setPlaying(false); setPos(0); setFailed(false) }, [src])

  const toggle = () => {
    const m = el.current
    if (!m) return
    if (m.paused) { m.play().catch(() => setFailed(true)); setPlaying(true) }
    else { m.pause(); setPlaying(false) }
  }

  const media = {
    ref: el,
    src: src ?? undefined,
    preload: 'metadata',
    onLoadedMetadata: (e) => setLen(e.target.duration || 0),
    onTimeUpdate: (e) => setPos(e.target.currentTime),
    onEnded: () => { setPlaying(false); setPos(0) },
    onError: () => setFailed(true),
  }

  // A .mov from an iPhone is often HEVC, which Safari plays and Chrome doesn't.
  // Say so plainly instead of showing a black rectangle.
  const unplayable = failed && (
    <p className="small" style={{ marginTop: 6 }}>
      This browser can't play {filename ? <b>{filename}</b> : 'this recording'}
      {mime === 'video/quicktime' && ' — iPhone video is often HEVC, which Safari plays but Chrome does not'}.
      {src && <> <a href={src} download style={{ color: 'var(--accent-deep)' }}>Download it</a> to listen.</>}
    </p>
  )

  if (video) {
    return (
      <>
        <div className="videowrap">
          {src && !failed
            ? <video {...media} controls playsInline />
            : <div className="videoempty">{failed ? 'Can’t play here' : 'No recording yet'}</div>}
        </div>
        {unplayable}
      </>
    )
  }

  const seekTo = (f) => {
    const m = el.current
    if (!m || !len) return
    m.currentTime = f * len
    setPos(f * len)   // don't wait for timeupdate; the bar would snap back first
  }

  return (
    <>
      <div className="player">
        <button className="play" onClick={toggle} disabled={!src || failed}
                aria-label={playing ? 'Pause' : 'Play'}>{playing ? '❚❚' : '▶'}</button>
        <span className="ptime">{src ? mmss(pos) : '—'}</span>
        <Waveform pos={pos} len={len} onSeek={seekTo} disabled={!src || failed} />
        <span className="ptime ptime-total">{src && len ? mmss(len) : '—'}</span>
        {src && <audio {...media} />}
      </div>
      {unplayable}
    </>
  )
}
